import { isNativeApp } from './native-session.js'
import { APP_URL_SCHEME, parseSignInReturn, type SignInReturn } from './sign-in-device.js'

/**
 * Sign-in returns into the installed app (DEV-022/023): Apple/Google come back from the system
 * browser, and the email's link page hands a code over, through the app's own URL scheme
 * (`com.instadyn.signbento://sign-in#…`, registered in `apps/native`).
 *
 * One listener for the whole app, started by the shell, so a return is handled exactly once:
 *
 *  * the sign-in screen, when it is open, receives it at once;
 *  * otherwise (the app was on another page, or the system relaunched it on Home after closing it
 *    in the background while the person was in their mail app) it is kept, the shell is asked to
 *    open the sign-in screen, and that screen takes it when it mounts;
 *  * the URL the app was launched with is read once per process, never again when the sign-in
 *    screen mounts later (after a sign-out, an old, spent code must not reappear).
 */

type Receiver = (arrival: SignInReturn) => void

let started = false
let kept: SignInReturn | null = null
const receivers = new Set<Receiver>()

/** What the shell does when a return arrives and no sign-in screen is open: open one. */
export type OpenSignIn = () => void

export function handleNativeSignInUrl(url: string, openSignIn: OpenSignIn): boolean {
  if (!url.startsWith(`${APP_URL_SCHEME}:`)) return false
  const hash = url.includes('#') ? url.slice(url.indexOf('#')) : ''
  const arrival = parseSignInReturn(hash)
  if (arrival === null) return false
  if (receivers.size > 0) {
    for (const receive of receivers) receive(arrival)
  } else {
    kept = arrival
    openSignIn()
  }
  return true
}

/** Starts the app's one listener; a browser (or a second call) does nothing. */
export async function startNativeSignInReturns(openSignIn: OpenSignIn): Promise<void> {
  if (started || !isNativeApp()) return
  started = true
  const [{ App }, { Browser }] = await Promise.all([
    import('@capacitor/app'),
    import('@capacitor/browser'),
  ])
  const receive = (url: string): void => {
    // The system browser that showed Apple's or Google's page goes away (iOS; Android's custom
    // tab is already behind the app).
    if (handleNativeSignInUrl(url, openSignIn)) void Browser.close().catch(() => undefined)
  }
  const launch = await App.getLaunchUrl().catch(() => undefined)
  if (launch?.url !== undefined) receive(launch.url)
  try {
    await App.addListener('appUrlOpen', (event) => receive(event.url))
  } catch {
    // A shell without the App plugin (an older build): the code can still be typed.
  }
}

/** The sign-in screen's subscription; returns its removal. */
export function onNativeSignInReturn(receive: Receiver): () => void {
  receivers.add(receive)
  return () => {
    receivers.delete(receive)
  }
}

/**
 * A return that arrived before the sign-in screen opened. Read without clearing (React may run a
 * state initialiser twice); the screen clears it once it acts on it.
 */
export function peekKeptSignInReturn(): SignInReturn | null {
  return kept
}

export function clearKeptSignInReturn(): void {
  kept = null
}

/** Tests only: forget the listener's state. */
export function resetNativeSignInReturnsForTests(): void {
  started = false
  kept = null
  receivers.clear()
}
